/**
 * LocationSelectionContext
 * Manages selection state for synced locations in the list view.
 * Used for bulk batch operations (auto-enrich, analyze tags, publish).
 */

import { createContext, useContext, useState, useCallback, useMemo } from 'react';
import type { ReactNode } from 'react';

interface LocationSelectionContextValue {
  /** Whether selection mode is active */
  isSelectionMode: boolean;
  /** Set of selected location IDs */
  selectedIds: Set<string>;
  /** Toggle selection mode on/off */
  toggleSelectionMode: () => void;
  /** Toggle a single location's selection */
  toggle: (id: string) => void;
  /** Select multiple locations at once */
  selectAll: (ids: string[]) => void;
  /** Deselect all locations */
  deselectAll: () => void;
  /** Clear selection and exit selection mode */
  clearSelection: () => void;
  /** Check if a location is selected */
  isSelected: (id: string) => boolean;
  /** Number of selected locations */
  selectionCount: number;
}

const LocationSelectionContext = createContext<LocationSelectionContextValue | null>(null);

interface LocationSelectionProviderProps {
  children: ReactNode;
}

export function LocationSelectionProvider({ children }: LocationSelectionProviderProps): JSX.Element {
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const toggleSelectionMode = useCallback(() => {
    setIsSelectionMode((prev) => {
      if (prev) {
        // Exiting selection mode - clear selections
        setSelectedIds(new Set());
      }
      return !prev;
    });
  }, []);

  const toggle = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const selectAll = useCallback((ids: string[]) => {
    setSelectedIds(new Set(ids));
  }, []);

  const deselectAll = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
    setIsSelectionMode(false);
  }, []);

  const isSelected = useCallback((id: string) => selectedIds.has(id), [selectedIds]);

  const selectionCount = selectedIds.size;

  const value = useMemo(
    () => ({
      isSelectionMode,
      selectedIds,
      toggleSelectionMode,
      toggle,
      selectAll,
      deselectAll,
      clearSelection,
      isSelected,
      selectionCount,
    }),
    [isSelectionMode, selectedIds, toggleSelectionMode, toggle, selectAll, deselectAll, clearSelection, isSelected, selectionCount]
  );

  return (
    <LocationSelectionContext.Provider value={value}>
      {children}
    </LocationSelectionContext.Provider>
  );
}

export function useLocationSelection(): LocationSelectionContextValue {
  const context = useContext(LocationSelectionContext);
  if (!context) {
    throw new Error('useLocationSelection must be used within a LocationSelectionProvider');
  }
  return context;
}
